<script setup>
import { ref, watch } from 'vue'
import { api } from '../../services/api'

const props = defineProps({ chapter: { type: Object, required: true }, semester: { type: Object, required: true }, subject: { type: Object, required: true } })
const emit = defineEmits(['changed', 'select'])
const blankLesson = () => ({ title: '', summary: '', duration_minutes: 20, is_published: true })
const blankQuiz = () => ({ title: '', quiz_type: 'practice', description: '', duration_minutes: 15 })
const chapterForm = ref({})
const lessonForm = ref(blankLesson())
const quizForm = ref(blankQuiz())
const editing = ref(false)
const saving = ref('')
const error = ref('')

watch(() => props.chapter, chapter => {
  chapterForm.value = { title: chapter.title, description: chapter.description || '' }
  error.value = ''
}, { immediate: true })
watch(() => props.chapter.id, () => { lessonForm.value = blankLesson(); quizForm.value = blankQuiz(); editing.value = false })

async function run(name, task) {
  if (saving.value) return
  saving.value = name
  error.value = ''
  try { await task() }
  catch (err) { error.value = err.message }
  finally { saving.value = '' }
}

const saveChapter = () => run('chapter', async () => {
  await api(`/admin/chapters/${props.chapter.id}`, { method: 'PUT', body: JSON.stringify(chapterForm.value) })
  editing.value = false
  emit('changed', { message: 'Đã lưu chương' })
})
const addLesson = () => run('lesson', async () => {
  const result = await api('/admin/lessons', { method: 'POST', body: JSON.stringify({ chapter_id: props.chapter.id, ...lessonForm.value }) })
  emit('changed', { select: `lesson-${result.lesson.id}`, message: 'Đã tạo bài học. Bổ sung câu hỏi luyện tập ngay bên dưới.' })
})
const addQuiz = () => run('quiz', async () => {
  await api('/admin/quizzes', { method: 'POST', body: JSON.stringify({ chapter_id: props.chapter.id, ...quizForm.value }) })
  quizForm.value = blankQuiz()
  emit('changed', { message: 'Đã tạo đề từ các câu hỏi trong chương' })
})
const togglePublished = lesson => run(`publish-${lesson.id}`, async () => {
  await api(`/admin/lessons/${lesson.id}`, { method: 'PUT', body: JSON.stringify({ is_published: !lesson.is_published }) })
  emit('changed', { message: lesson.is_published ? 'Đã ẩn bài khỏi học sinh' : 'Đã xuất bản bài học' })
})
</script>

<template>
  <div class="panel-body">
    <header class="panel-head">
      <div><p class="eyebrow">Chương / chủ đề</p><h2>{{ chapter.title }}</h2><p v-if="chapter.description && !editing" class="panel-hint">{{ chapter.description }}</p></div>
      <button v-if="!editing" type="button" class="btn btn-light btn-sm" @click="editing = true">Sửa tên chương</button>
    </header>
    <div v-if="error" class="error-banner" role="alert">{{ error }}</div>

    <form v-if="editing" class="panel-section panel-form" @submit.prevent="saveChapter">
      <div class="form-group"><label for="chapter-edit-title">Tên chương</label><input id="chapter-edit-title" v-model="chapterForm.title" class="form-control" required maxlength="180" :disabled="!!saving"></div>
      <div class="form-group"><label for="chapter-edit-description">Mô tả</label><textarea id="chapter-edit-description" v-model="chapterForm.description" class="form-control" :disabled="!!saving"></textarea></div>
      <div class="panel-actions"><button class="btn btn-primary" :disabled="!!saving">{{ saving === 'chapter' ? 'Đang lưu…' : 'Lưu chương' }}</button><button type="button" class="btn btn-light" :disabled="!!saving" @click="editing = false">Hủy</button></div>
    </form>

    <section class="panel-section">
      <div class="panel-section-head"><h3>Bài học</h3><span>{{ chapter.lessons.length }}</span></div>
      <p v-if="!chapter.lessons.length" class="panel-hint">Chương chưa có bài. Thêm bài đầu tiên ở dưới.</p>
      <div v-for="(lesson, index) in chapter.lessons" :key="lesson.id" class="panel-item static">
        <i>{{ index + 1 }}</i>
        <button type="button" class="panel-item-main" @click="emit('select', `lesson-${lesson.id}`)"><strong>{{ lesson.title }}</strong><small>{{ lesson.duration_minutes }} phút · {{ lesson.question_count }} câu<template v-if="!lesson.is_published"> · <em>Nháp</em></template></small></button>
        <button type="button" class="text-link" :disabled="!!saving" @click="togglePublished(lesson)">{{ saving === `publish-${lesson.id}` ? 'Đang lưu…' : lesson.is_published ? 'Ẩn' : 'Xuất bản' }}</button>
        <button type="button" class="text-link" @click="emit('select', `lesson-${lesson.id}`)">Sửa →</button>
      </div>
      <div v-if="chapter.quizzes.length" class="panel-subnote">Đề kèm chương: <span v-for="quiz in chapter.quizzes" :key="quiz.id">{{ quiz.title }} ({{ quiz.question_count }} câu)</span></div>
    </section>

    <form class="panel-section panel-form" @submit.prevent="addLesson">
      <div class="panel-section-head"><h3>＋ Thêm bài học vào chương này</h3></div>
      <div class="form-grid">
        <div class="form-group wide"><label for="lesson-new-title">Tên bài học</label><input id="lesson-new-title" v-model="lessonForm.title" class="form-control" required :disabled="!!saving" placeholder="Ví dụ: Phép cộng phân số"></div>
        <div class="form-group wide"><label for="lesson-new-summary">Mô tả ngắn</label><textarea id="lesson-new-summary" v-model="lessonForm.summary" class="form-control" :disabled="!!saving" placeholder="Mục tiêu và nội dung chính…"></textarea></div>
        <div class="form-group"><label for="lesson-new-duration">Thời lượng (phút)</label><input id="lesson-new-duration" v-model.number="lessonForm.duration_minutes" class="form-control" type="number" min="1" max="180" :disabled="!!saving"></div>
        <label class="check-line"><input v-model="lessonForm.is_published" type="checkbox" :disabled="!!saving"><span>Xuất bản ngay</span></label>
      </div>
      <button class="btn btn-primary" :disabled="!!saving">{{ saving === 'lesson' ? 'Đang tạo…' : 'Tạo bài học' }}</button>
    </form>

    <details class="panel-section panel-form panel-details">
      <summary>＋ Tạo đề kiểm tra từ câu hỏi của chương</summary>
      <form @submit.prevent="addQuiz">
        <div class="form-grid">
          <div class="form-group wide"><label for="quiz-new-title">Tên đề</label><input id="quiz-new-title" v-model="quizForm.title" class="form-control" required :disabled="!!saving"></div>
          <div class="form-group"><label for="quiz-new-type">Loại đề</label><select id="quiz-new-type" v-model="quizForm.quiz_type" class="form-control" :disabled="!!saving"><option value="practice">Luyện tập</option><option value="15_minute">Kiểm tra 15 phút</option><option value="midterm">Giữa kỳ</option><option value="final">Cuối kỳ</option></select></div>
          <div class="form-group"><label for="quiz-new-duration">Thời lượng (phút)</label><input id="quiz-new-duration" v-model.number="quizForm.duration_minutes" class="form-control" type="number" min="1" :disabled="!!saving"></div>
          <div class="form-group wide"><label for="quiz-new-description">Mô tả</label><textarea id="quiz-new-description" v-model="quizForm.description" class="form-control" :disabled="!!saving"></textarea></div>
        </div>
        <button class="btn btn-light" :disabled="!!saving">{{ saving === 'quiz' ? 'Đang tạo…' : 'Tạo đề' }}</button>
        <p class="panel-hint">Đề mới tự lấy toàn bộ câu hỏi luyện tập hiện có của các bài trong chương.</p>
      </form>
    </details>
  </div>
</template>
